/**
 * BannedProductWarning
 * --------------------
 * Reusable warning that scans a list of product name strings against the
 * banned drug database and displays a prominent red banner + tooltip.
 *
 * Also exports `useBannedCheck(productNames)` — a hook returning a Set of
 * matched inputs so parent tables can highlight banned rows in red.
 *
 * Usage:
 *   const { matches, matchedSet } = useBannedCheck(productNamesArray);
 *   <BannedProductWarning matches={matches} />
 *   ...
 *   <tr className={matchedSet.has(productName) ? 'bg-red-50 text-red-700 font-medium' : ''}>...</tr>
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { AlertTriangle, ShieldOff } from "lucide-react";

export const useBannedCheck = (productNames) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);

  const stableKey = useMemo(
    () => (productNames || []).slice().sort().join("||"),
    [productNames]
  );

  useEffect(() => {
    const arr = (productNames || []).filter(Boolean);
    if (arr.length === 0) { setMatches([]); return; }
    setLoading(true);
    let cancelled = false;
    axios.post(`${API}/banned-products/check`,
      { product_names: arr },
      { headers: getAuthHeader() })
      .then(r => { if (!cancelled) setMatches(r.data.matches || []); })
      .catch(() => { if (!cancelled) setMatches([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [stableKey]);

  const matchedSet = useMemo(() => {
    const s = new Set();
    matches.forEach(m => s.add(m.input));
    return s;
  }, [matches]);

  const isBanned = (name) => matchedSet.has(name);

  return { matches, matchedSet, isBanned, loading };
};

export const BannedProductWarning = ({ matches, compact = false }) => {
  if (!matches || matches.length === 0) return null;
  return (
    <div
      className={`border-2 border-red-400 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg ${compact ? "p-3" : "p-4"} mb-3 shadow-sm`}
      role="alert"
      data-testid="banned-product-warning"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-full bg-red-500 text-white flex items-center justify-center">
          <ShieldOff className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`font-bold text-red-800 ${compact ? "text-sm" : "text-base"}`}>
              ⚠ BANNED PRODUCTS DETECTED — NOT FOR SALE
            </h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-600 text-white font-semibold">
              {matches.length} match{matches.length > 1 ? "es" : ""}
            </span>
          </div>
          <p className="text-xs text-red-700 mt-1">
            Processing cannot continue until reviewed by an administrator.
          </p>
          <div className="mt-2 space-y-1">
            {matches.slice(0, 8).map((m, i) => (
              <div key={i} className="text-xs bg-white/70 rounded px-2 py-1 border border-red-200">
                <span className="font-mono text-red-800 font-semibold">{m.input}</span>
                <span className="text-red-500 mx-1">→</span>
                <span className="text-red-700">
                  <b>{m.banned_product_name}</b> ({m.banned_generic_name})
                </span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800 ml-2">{m.drug_class}</span>
              </div>
            ))}
            {matches.length > 8 && (
              <div className="text-xs text-red-600 italic">…and {matches.length - 8} more.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Parse a document-generation error response and extract banned-product-block info.
 * Returns { isBlocked: boolean, message: string, matches: [], grouped: [] }.
 */
export const parseBannedBlockError = (error) => {
  const detail = error?.response?.data?.detail;
  const isBlocked = error?.response?.status === 409
    && typeof detail === "object"
    && detail?.error === "banned_products_detected";
  if (!isBlocked) return { isBlocked: false };
  return {
    isBlocked: true,
    message: detail.message || "Banned products detected — generation blocked.",
    documentType: detail.document_type || "Document",
    matches: detail.matches || [],
    grouped: detail.grouped || [],
    totalMatches: detail.total_matches || (detail.matches?.length || 0),
  };
};

/**
 * Blocking modal-style banner for showing a 409 block error inline near the
 * generation button. Persistent until user removes the banned product or the
 * source file. Use with parseBannedBlockError() from the axios catch block.
 */
export const BannedBlockBanner = ({ block, onClose }) => {
  if (!block || !block.isBlocked) return null;
  return (
    <div
      className="border-2 border-red-500 bg-gradient-to-r from-red-50 to-red-100 rounded-lg p-4 mb-3 shadow-lg animate-in fade-in"
      role="alert"
      data-testid="banned-block-banner"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center">
          <ShieldOff className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-bold text-red-900 text-base">
                {block.documentType} generation BLOCKED
              </h3>
              <p className="text-sm text-red-800 mt-1">{block.message}</p>
            </div>
            {onClose && (
              <button onClick={onClose}
                      className="text-red-500 hover:text-red-700 text-xl font-bold shrink-0"
                      data-testid="banned-block-close" aria-label="Dismiss">×</button>
            )}
          </div>
          <div className="mt-3 space-y-1 max-h-64 overflow-y-auto">
            {(block.grouped || []).slice(0, 15).map((g, i) => (
              <div key={i} className="text-xs bg-white rounded px-2 py-1.5 border border-red-300"
                   data-testid={`banned-block-item-${i}`}>
                <div className="flex flex-wrap items-center gap-1.5">
                  <ShieldOff className="w-3 h-3 text-red-600" />
                  <span className="font-semibold text-red-900">{g.banned_product_name}</span>
                  <span className="text-red-600">({g.banned_generic_name})</span>
                  <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-red-200 text-red-900 font-semibold">
                    {g.drug_class}
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-slate-700 ml-4">
                  <span className="text-slate-500">Found in source: </span>
                  {(g.inputs || []).map((inp, j) => (
                    <span key={j} className="inline-block font-mono bg-red-50 text-red-800 px-1.5 py-0.5 rounded mr-1 mb-0.5 border border-red-200">
                      {inp}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {(block.grouped || []).length > 15 && (
              <div className="text-xs text-red-700 italic">…and {block.grouped.length - 15} more banned products.</div>
            )}
          </div>
          <div className="mt-3 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded p-2">
            <b>What to do:</b> Remove the banned products from the source Excel/PDF and try again,
            or contact your administrator to review the Banned Product Info list.
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannedProductWarning;
